import { useContext, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import ArtworkDataContext from '@/context/ArtworkDataContext';
import useGoBack from '@/hooks/useGoBack';
import useNavigateWithTransition from '@/hooks/useNavigateWithTransition';
import useStateHandler from '@/hooks/useStateHandler';
import { buildArtist } from '@/pages/artwork/content-section/components/artworkMoreDetailsDesktop.helpers';
import {
  getArtworkArtistModalData,
  getArtworkViewerContext,
  isViewerDisplayedArtistOwner,
} from '@/pages/artwork/utils/artworkView.selectors';
import ArtworksOfArtist from '@/pages/profile/artists/artworks-of-artist';
import {
  ARTWORK_PREFERENCE_SIGNALS,
  trackArtworkPreferenceInteraction,
} from '@/services/artworkPreferenceInteraction.service';
import FollowButton from '@/shared-components/buttons/components/FollowButton';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import BackArrowIcon from '@/shared-components/icons/components/public/BackArrowIcon';
import Text from '@/shared-components/text/components/Text';

import './index.css';

export default function ArtworkHeaderMobile({ className, showGoBack = true }) {
  const { artworkData } = useContext(ArtworkDataContext);
  const { state } = useStateHandler();
  const imageWrapperRef = useRef(null);
  const goBackHandler = useGoBack("/");
  const { pathname } = useLocation();
  const navigateWithTransition = useNavigateWithTransition();

  const artworkAbout = artworkData?.artwork_about;
  const viewerContext = getArtworkViewerContext(artworkData);
  const artist = useMemo(
    () => buildArtist(artworkAbout, viewerContext),
    [artworkAbout, viewerContext?.artist, viewerContext?.gallery_artist],
  );
  const [isArtistArtworksOpen, setIsArtistArtworksOpen] = useState(false);
  const [modalQueryData, setModalQueryData] = useState({ cacheKey: '', endpoint: '' });

  const cacheKeyTpl = 'artworks_of_{0}_in_search';
  const endpointArtist = '/galleryartist/{0}/artworks';
  const endpointGalleryArtist = '/galleryartist/{0}/artworks';

  const artistModalData = useMemo(() => getArtworkArtistModalData(artworkData), [artworkData]);

  const openArtistArtworks = () => {
    if (!artistModalData || !artistModalData._id) return;

    if (artworkData?.slug) {
      trackArtworkPreferenceInteraction({
        slug: artworkData.slug,
        signal: ARTWORK_PREFERENCE_SIGNALS.ARTIST_PROFILE_VIEW_FROM_ARTWORK,
        metadata: { source: 'artwork-view-header__artist-container' },
      });
    }

    const forcedType = artistModalData.user_type === 'gallery_artist' ? 'gallery_artist' : 'artist';
    const cacheKey = cacheKeyTpl.replace('{0}', artistModalData._id);
    const endpoint =
      forcedType === 'gallery_artist'
        ? endpointGalleryArtist.replace('{0}', artistModalData._id)
        : endpointArtist.replace('{0}', artistModalData._id);

    setModalQueryData({ cacheKey, endpoint });
    setIsArtistArtworksOpen(true);
  };

  const isArtistOwner = isViewerDisplayedArtistOwner(artworkData, state.user_session);
  const canFollowArtist = Boolean(artist.id) && !isArtistOwner;

  const containerClassName = [
    'artwork-view-header__container',
    'element-non-selectable',
    !showGoBack && 'artwork-view-header__container--no-goback',
    className,
  ].filter(Boolean).join(' ');

  return (
    <>
      <div className={containerClassName}>
        <div className="artwork-view-header__inner-container">
          {showGoBack && (
            <BackArrowIcon
              className={`artwork-view-header__goBack ${pathname?.includes("suarteworld") ? 'suarteworld' : ''}`}
              onClick={goBackHandler}
            />
          )}
          <div
            className="artwork-view-header__artist-container"
            onClick={() => {
              if (!artist?.username) {
                openArtistArtworks();
                return;
              }

              if (artworkData?.slug) {
                trackArtworkPreferenceInteraction({
                  slug: artworkData.slug,
                  signal: ARTWORK_PREFERENCE_SIGNALS.ARTIST_PROFILE_VIEW_FROM_ARTWORK,
                  metadata: { source: 'artwork-view-header__artist-container' },
                });
              }

              if (imageWrapperRef.current && artist?.id) {
                imageWrapperRef.current.style.viewTransitionName = `user_${artist.id}_animation`;
              }

              navigateWithTransition(`/user/${artist.username}`, {
                state: {
                  from: true,
                },
              });
            }}
          >
            <div className="artwork-view-header__artist-image-wrapper" ref={imageWrapperRef}>
              <UserProfileImage
                typeOfProfile={artist?.type || artist?.user_type}
                image={artist?.image || artist?.user_image?.image_id}
                className="artwork-view-header__artist-image"
              />
            </div>
            <div className="artwork-header-mobile__texts">
              <Text
                className="artwork-view-header__artist-name dots-on-overflow font-size-large"
                paragraph>
                {artist?.name || artist?.user_name || '-'}
              </Text>
              <Text
                className="artwork-header-mobile__artist-location dots-on-overflow font-size-medium"
                paragraph>
                {artist?.location || ''}
              </Text>
            </div>
          </div>
          {canFollowArtist && (
            <FollowButton
              className="artwork-view__follow-button artwork-more-details__desktop__follow-button artwork-view-header__follow-button font-size-small"
              userId={artist.id}
              followeeType={artist?.type === 'gallery_artist' ? 'gallery_artist' : 'user'}
              imFollowing={artist.imFollowing}
              isFollowingMe={artist.isFollowingMe}
              userName={artist.name}
              userUsername={artist.username}
              showFollowingIcon />
          )}
        </div>
      </div>
      {artistModalData && (
        <ArtworksOfArtist
          open={isArtistArtworksOpen}
          close={() => setIsArtistArtworksOpen(false)}
          artist={artistModalData}
          profileData={artistModalData}
          internal={false}
          showHeader={false}
          cacheKey={modalQueryData.cacheKey}
          queryEndpoint={modalQueryData.endpoint}
        />
      )}
    </>
  );
}
